import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Import types for each content section
import type { ChefSpecialContent } from "@/types/chefsSpecial.types";
import type {
  AppDownloadContent,
  CTASideBySideContent,
} from "@/types/cta.types";
import type { FooterContent } from "@/types/footer.types";
import type { GalleryContent } from "@/types/gallery.types";
import type { HeroContent } from "@/types/hero.types";
import type { InstagramContent } from "@/types/instagram.types";
import type { MenuEditorialContent } from "@/types/menu.types";

/**
 * Site Metadata Interface
 */
export interface SiteMetadata {
  siteName: string;
  description: string;
  keywords?: string[];
  email?: string;
  phone?: string;
  favicon?: string;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
}

/**
 * Unified Content Interface
 * Represents the structure of the entire content.json file
 */
export interface UnifiedContent {
  [key: string]: any;
  hero: HeroContent;
  appDownload: AppDownloadContent;
  ctaSideBySide: CTASideBySideContent;
  chefsSpecial: ChefSpecialContent;
  footer: FooterContent;
  gallery: GalleryContent;
  instagram: InstagramContent;
  menuEditorial: MenuEditorialContent;
  metaData?: SiteMetadata;
}

/**
 * Content State Interface
 */
export interface ContentState {
  data: UnifiedContent | null;
  loading: boolean;
  error: string | null;
}

/**
 * Initial State
 */
const initialState: ContentState = {
  data: null,
  loading: false,
  error: null,
};

/**
 * Content Slice
 */
const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {
    // Set unified content data
    setContent: (state, action: PayloadAction<UnifiedContent>) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },

    // Set loading state
    setContentLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error state
    setContentError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Reset content state
    resetContent: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
  },
});

// Export actions
export const { setContent, setContentLoading, setContentError, resetContent } =
  contentSlice.actions;

// Export reducer
export default contentSlice.reducer;
